import React, { useState } from 'react'
import { Button, Card, Space, Typography, message, Tooltip, Input } from 'antd'
import { CopyOutlined, ReloadOutlined, NumberOutlined } from '@ant-design/icons'

const { Text, Title } = Typography

interface SimulationIdGeneratorProps {
  onIdGenerated?: (id: string) => void
  title?: string
  size?: 'small' | 'middle' | 'large'
}

const SimulationIdGenerator: React.FC<SimulationIdGeneratorProps> = ({ 
  onIdGenerated, 
  title = "Generador de Simulation IDs",
  size = 'middle'
}) => {
  const [generatedIds, setGeneratedIds] = useState<string[]>([])
  const [currentId, setCurrentId] = useState<string>('')

  // Solo IDs reales confirmados en SEAS QA
  const validSimulationIds = [
    '745437',  // COLFONDOS · Pensionado · Score 751
    '745148',  // SED BOGOTA · Pensionado · Score 668
    '745147',  // COLPENSIONES · Propiedad · Score 471
  ]

  const generateRandomId = () => {
    message.warning('Solo los IDs de SEAS QA funcionan. Usa "Usar ID Válido".')
  }

  const useValidId = () => {
    const validId = validSimulationIds[Math.floor(Math.random() * validSimulationIds.length)]
    setCurrentId(validId)
    setGeneratedIds(prev => [validId, ...prev.slice(0, 4)])
    onIdGenerated?.(validId)
    message.success(`ID seleccionado: ${validId}`)
  }

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id)
    message.success('ID copiado al portapapeles')
  }

  const useCustomId = (customId: string) => {
    if (customId.length >= 4 && /^\d+$/.test(customId)) {
      setCurrentId(customId)
      setGeneratedIds(prev => [customId, ...prev.slice(0, 4)])
      onIdGenerated?.(customId)
      message.success(`ID personalizado: ${customId}`)
    } else {
      message.error('El ID debe contener solo números y tener al menos 4 dígitos')
    }
  }

  return (
    <Card 
      title={title} 
      size={size}
      extra={
        <Tooltip title="Los simulation_ids son necesarios para probar endpoints">
          <NumberOutlined style={{ color: '#1890ff' }} />
        </Tooltip>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        
        {/* Botones principales */}
        <Space wrap>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={generateRandomId}
          >
            Generar Aleatorio
          </Button>
          
          <Button 
            type="default"
            icon={<NumberOutlined />}
            onClick={useValidId}
          >
            Usar ID Válido
          </Button>
        </Space>

        {/* ID actual */}
        {currentId && (
          <div>
            <Text strong>ID Actual: </Text>
            <Space>
              <Text code style={{ fontSize: '16px', padding: '4px 8px' }}>
                {currentId}
              </Text>
              <Button 
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(currentId)}
                type="text"
              />
            </Space>
          </div>
        )}

        {/* Input personalizado */}
        <div>
          <Text type="secondary">O ingresa un ID personalizado:</Text>
          <Input.Search
            placeholder="Ej: 745437"
            enterButton="Usar"
            size="small"
            style={{ marginTop: 4 }}
            onSearch={useCustomId}
          />
        </div>

        {/* Historial de IDs recientes */}
        {generatedIds.length > 0 && (
          <div>
            <Text type="secondary">IDs Recientes:</Text>
            <div style={{ marginTop: 4 }}>
              {generatedIds.map((id, index) => (
                <Space key={`${id}-${index}`} style={{ marginRight: 8, marginBottom: 4 }}>
                  <Text code style={{ fontSize: '12px' }}>{id}</Text>
                  <Button 
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(id)}
                    style={{ padding: '0 4px' }}
                  />
                </Space>
              ))}
            </div>
          </div>
        )}

        {/* IDs de prueba recomendados */}
        <div>
          <Text type="secondary">IDs de Prueba Recomendados:</Text>
          <div style={{ marginTop: 4 }}>
            {validSimulationIds.slice(0, 3).map(id => (
              <Space key={id} style={{ marginRight: 8, marginBottom: 4 }}>
                <Text code style={{ fontSize: '12px', color: '#52c41a' }}>{id}</Text>
                <Button 
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(id)}
                  style={{ padding: '0 4px' }}
                />
              </Space>
            ))}
          </div>
        </div>

      </Space>
    </Card>
  )
}

export default SimulationIdGenerator